import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import PermissionResourceSchema from "permissions/resources/_schemas/permission-resource-schema";
import { AccessLevel, ResourceType } from "permissions/resources/permission-resource-types";
import { z } from "zod";

export type DocumentAccessJSON = z.infer<typeof DocumentAccessSchema.DocumentAccessJSONZ>;

export type DocumentAccessParams = z.infer<typeof DocumentAccessSchema.DocumentAccessZ>;

export default class DocumentAccessSchema {
  public static readonly DocumentAccessJSONZ = z.object({
    ...PermissionResourceSchema.ResourceJSONZ.shape,
    resourceType: z.literal(ResourceType.DocumentAccess),
    accessLevel: z.enum(AccessLevel).nullish(),
    documentId: z.uuid().nullish(),
  });

  public static readonly DocumentAccessZ = z.object({
    ...PermissionResourceSchema.ResourceZ.shape,
    resourceType: z.literal(ResourceType.DocumentAccess),
    accessLevel: z.enum(AccessLevel).optional(),
    documentId: z.instanceof(Guid).optional(),
  });

  public static validate(values: unknown): DocumentAccessParams {
    return parseOrThrow(DocumentAccessSchema.DocumentAccessZ, values, {
      logError: true,
      schemaName: "DocumentAccess",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): DocumentAccessJSON {
    return parseOrThrow(DocumentAccessSchema.DocumentAccessJSONZ, values, {
      logError: true,
      schemaName: "DocumentAccess",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is DocumentAccessParams {
    try {
      DocumentAccessSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is DocumentAccessJSON {
    try {
      DocumentAccessSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: DocumentAccessJSON): DocumentAccessParams {
    const parsed = parseOrThrow(DocumentAccessSchema.DocumentAccessJSONZ, data, {
      logError: true,
      schemaName: "DocumentAccess",
      operation: "deserialize",
      payloadSample: data,
    });
    return DocumentAccessSchema.codec.decode(parsed);
  }

  public static serialize(notification: DocumentAccessParams): DocumentAccessJSON {
    const parsed = DocumentAccessSchema.validate(notification);
    return DocumentAccessSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    DocumentAccessSchema.DocumentAccessJSONZ,
    DocumentAccessSchema.DocumentAccessZ,
    {
      decode: (data) => ({
        ...PermissionResourceSchema.deserialize(data),
        resourceType: ResourceType.DocumentAccess as const,
        accessLevel: data.accessLevel ?? undefined,
        documentId: Guid.fromJSON(data.documentId) ?? undefined,
      }),
      encode: (resource) => ({
        ...PermissionResourceSchema.serialize(resource),
        resourceType: ResourceType.DocumentAccess as const,
        accessLevel: resource.accessLevel,
        documentId: resource.documentId?.toJSON(),
      }),
    },
  );
}
